/* SPEC-462 — the app shell's pure rules: which environment this is, what the ONE health
 * dot says, and which global actions the left rail offers this login.
 *
 * Pure, no React, no `@/` alias — asserted by `config/shell.test.ts` under Node's type
 * stripping (the same pattern as `nav-groups.ts`).
 *
 * Shell invariants (frontend/src/CONTEXT.md):
 *   - the rail carries GLOBAL verbs only — never a page, never a per-page icon;
 *   - the status bar carries SYSTEM state only — never a page's data date (each page's
 *     DataFreshnessRibbon owns that; SPEC-453: two dates on one screen make a reader
 *     doubt both).
 */

/* ── Environment badge ──────────────────────────────────────────────────────────── */

export type ShellEnvironment = 'production' | 'staging' | 'local';

/** The environment, from the hostname. This is the signal the app already uses
 *  (`lib/workspace.getSubdomain` treats `staging.` the same way); there is no backend
 *  environment endpoint, and inventing one is out of scope.
 *  - `staging.<domain>`           → staging
 *  - localhost / an IP / no dot   → local
 *  - any other host               → production (suryaiq.com, www., workspace subdomains)
 */
export function environmentFor(hostname: string): ShellEnvironment {
  const host = (hostname || '').toLowerCase();
  if (!host || host === 'localhost' || !host.includes('.') || /^[\d.]+$/.test(host) || host.startsWith('[')) {
    return 'local';
  }
  if (host.split('.')[0] === 'staging') return 'staging';
  return 'production';
}

export const ENVIRONMENT_LABEL: Record<ShellEnvironment, string> = {
  production: 'Production',
  staging: 'Staging',
  local: 'Local',
};

/* ── The ONE health dot ─────────────────────────────────────────────────────────── */

export type ShellHealthState = 'healthy' | 'caution' | 'degraded' | 'unknown';

export interface ShellHealth {
  state: ShellHealthState;
  /** False when the rollup could not be read at all — "health unknown", never green. */
  reachable: boolean;
  /** Up to two plain-language reasons from the rollup, for the tooltip. */
  reasons: string[];
  /** Why a HEALTHY state must not read as a clean "normal" (review finding 2), or null. */
  qualifier: 'waiting_on_source' | 'integrity_unconfirmed' | null;
}

const KNOWN: ReadonlySet<string> = new Set(['healthy', 'caution', 'degraded', 'unknown']);

/** Read `GET /api/observability/health` (the SPEC-448 rollup) into the dot's state.
 *  `rollup === null` means the call failed or has not answered: that is UNKNOWN and
 *  says so. An unrecognised state also fails to unknown — a backend that grows a fifth
 *  state must render grey, not a confident green (the healthMeta rule). */
export function healthFromRollup(rollup: unknown): ShellHealth {
  if (!rollup || typeof rollup !== 'object') {
    return { state: 'unknown', reachable: false, reasons: [], qualifier: null };
  }
  const r = rollup as {
    state?: unknown; reasons?: unknown; source_faults_open?: unknown;
    planning_safe?: unknown; integrity_available?: unknown;
  };
  let state = typeof r.state === 'string' && KNOWN.has(r.state)
    ? (r.state as ShellHealthState)
    : 'unknown';
  const reasons = Array.isArray(r.reasons)
    ? r.reasons.filter((x): x is string => typeof x === 'string').slice(0, 2)
    : [];
  // The integrity verdict says planning is NOT safe: the one dot must not stay green or
  // grey beside that. Worst-of, the same rule the rollup applies to its nodes.
  if (r.planning_safe === false && (state === 'healthy' || state === 'unknown')) {
    state = 'caution';
    reasons.unshift('The integrity check says planning is not safe right now');
  }
  // NK-129: open source faults do not change a STATE, so the WORDS carry them — exactly
  // what `healthMeta.stateLabel` does on the map. An unreadable integrity verdict likewise
  // must not let "healthy" read as fully confirmed.
  let qualifier: ShellHealth['qualifier'] = null;
  if (state === 'healthy') {
    if (r.integrity_available === false) qualifier = 'integrity_unconfirmed';
    else if (typeof r.source_faults_open === 'number' && r.source_faults_open > 0) qualifier = 'waiting_on_source';
  }
  return { state, reachable: true, reasons: reasons.slice(0, 2), qualifier };
}

/** The words beside the dot. Colour is never the only cue (WCAG 1.4.1). */
export function healthLabel(h: ShellHealth): string {
  if (!h.reachable) return 'Health unknown';
  switch (h.state) {
    case 'healthy':
      if (h.qualifier === 'integrity_unconfirmed') return 'Normal, integrity not confirmed';
      if (h.qualifier === 'waiting_on_source') return 'Normal, waiting on the source';
      return 'Systems normal';
    case 'caution': return 'Worth a look';
    case 'degraded': return 'Needs attention';
    default: return 'Not fully measured';
  }
}

/* ── Rail items ─────────────────────────────────────────────────────────────────── */

export type RailItemId = 'search' | 'decisions' | 'trust' | 'help' | 'admin';

/** The rail's global actions for this login, top to bottom, under the brand mark and the
 *  menu toggle. The foot cluster and the account control sit at the foot of the rail and
 *  are not in this list.
 *  - `pages` is the already role+workspace-filtered page list the sidebar uses, so a
 *    link the rail offers is a page the nav offers too.
 *  - Admin is gated on the REAL role (preview never overrides `role`) AND on the page
 *    being visible — hidden otherwise, never disabled.
 *  - Search and Decisions are verbs, not pages: every login has both. A login with no
 *    pending decisions still gets the button; the desk says "Nothing waiting".
 *  - SPEC-509: Overview is no longer an item — the brand mark IS the Overview link
 *    (`brandLinksHome`) — and Alerts moved to the foot cluster (`railFootItems`). */
export function railItems(role: string | undefined, pages: readonly string[]): RailItemId[] {
  const items: RailItemId[] = ['search', 'decisions'];
  // SPEC-535: the Trust button (was Methodology) lands on Docs, first page of the Trust group,
  // so it follows the Docs page — no /docs page, no Trust button, never a dead link.
  if (pages.includes('/docs')) items.push('trust');
  // SPEC-503: "Help for this page" opens the Docs page at the guide for the current screen.
  // It follows the page list the same way — no /docs page, no help button.
  if (pages.includes('/docs')) items.push('help');
  if ((role === 'admin' || role === 'super_admin') && pages.includes('/admin')) items.push('admin');
  return items;
}

/** SPEC-509: the rail mark and the sidebar logo link to Overview — only where the login can
 *  see Overview. Otherwise the mark stays a plain brand mark: never a dead link. */
export function brandLinksHome(pages: readonly string[]): boolean {
  return pages.includes('/');
}

export type RailFootItemId = 'alerts' | 'tours' | 'feedback';

/** SPEC-509: the foot cluster above Account, top to bottom. Alerts is a page, so it follows
 *  the page list; Walk-throughs and Feedback are verbs every login has (the tour picker
 *  self-filters, and the Feedback panel is open to everyone). Phones render the same list as
 *  labelled rows at the foot of the overlay menu. */
export function railFootItems(pages: readonly string[]): RailFootItemId[] {
  const items: RailFootItemId[] = [];
  if (pages.includes('/alerts')) items.push('alerts');
  items.push('tours', 'feedback');
  return items;
}

/** The event the Feedback button fires. SPEC-506 owns the panel and listens for it. */
export const OPEN_FEEDBACK_EVENT = 'suryaiq:open-feedback';

/** Fire the Feedback event and nothing else. `target` is `window` in the app and an explicit
 *  fake in the test. */
export function openFeedback(target: { dispatchEvent: (e: Event) => boolean }, source: 'rail'): void {
  target.dispatchEvent(new CustomEvent(OPEN_FEEDBACK_EVENT, { detail: { source } }));
}

/* ── The Alerts badge: two feeds, ONE badge, never a sum (SPEC-509) ────────────────── */

export interface AlertsFeeds {
  /** Critical findings in this login's remit nobody has looked at; null = not read yet. */
  critical: number | null;
  criticalError: boolean;
  /** Unread updates (the old top-bar bell's feed); null = not read yet. */
  updatesUnread: number | null;
  updatesError: boolean;
}

export type AlertsBadge = { kind: 'count'; text: string } | { kind: 'dot' } | { kind: 'error' } | null;

/** The one badge on the Alerts button. Precedence: red number › amber dot › "!".
 *  - Critical findings waiting → the red count. Unread updates never add to it.
 *  - The CRITICAL feed failed → "!", even with updates unread: a dot would say "no critical
 *    alerts", which a failed load cannot claim (a failed load is never a clean zero).
 *  - No critical findings and updates unread → the amber dot, no number.
 *  - No critical findings and the updates feed failed → "!".
 *  - Otherwise no badge. */
export function alertsBadge(f: AlertsFeeds): AlertsBadge {
  if (f.criticalError) return { kind: 'error' };
  const critical = f.critical ?? 0;
  if (critical > 0) return { kind: 'count', text: critical > 9 ? '9+' : String(critical) };
  if ((f.updatesUnread ?? 0) > 0) return { kind: 'dot' };
  if (f.updatesError) return { kind: 'error' };
  return null;
}

/** The Alerts button's accessible name: both feeds, in words. Colour is never the only cue. */
export function alertsLabel(f: AlertsFeeds): string {
  const unread = f.updatesUnread ?? 0;
  const updates = f.updatesError
    ? 'updates could not load'
    : f.updatesUnread === null
      ? 'checking for updates'
      : unread > 0
        ? `${unread} update${unread === 1 ? '' : 's'} unread`
        : null;
  // "Nothing waiting" only when BOTH feeds have answered zero — a feed still loading is not 0.
  if (!f.criticalError && f.critical === 0 && !updates) return 'Alerts — nothing waiting';
  const critical = f.criticalError
    ? 'could not load the critical count'
    : f.critical === null
      ? 'what needs a look'
      : f.critical > 0
        ? `${f.critical} critical not yet looked at`
        : 'no critical alerts';
  return `Alerts — ${critical}${updates ? `, ${updates}` : ''}`;
}

/* ── The "/" shortcut for Ask SuryaIQ (SPEC-509) ───────────────────────────────────── */

/** Where "/" belongs to something else: any dialog or modal (the walk-through picker, the
 *  feedback snip and markup overlays, the Decisions desk), any feedback overlay root, and
 *  any element that opts out with `data-shortcut-scope`. */
export const ASK_SHORTCUT_BLOCKERS = '[role="dialog"], [aria-modal="true"], [data-feedback-ui], [data-shortcut-scope]';

/** Does this keydown open the assistant? Only a bare "/" (no Ctrl, Cmd or Alt) that nothing
 *  else has handled (`defaultPrevented`), never while the reader is typing in a field (a "/"
 *  typed into a box is a character), never while focus sits inside a dialog, overlay or
 *  opted-out scope (`ASK_SHORTCUT_BLOCKERS`), and never while a modal is open at all
 *  (`modalOpen`) — the assistant must not open on top of, or underneath, another layer. */
export function isAskShortcut(e: {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  defaultPrevented?: boolean;
  modalOpen?: boolean;
  target: { tagName?: string; isContentEditable?: boolean; closest?: (selector: string) => unknown } | null;
}): boolean {
  if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return false;
  if (e.defaultPrevented || e.modalOpen) return false;
  if (e.target?.closest && e.target.closest(ASK_SHORTCUT_BLOCKERS)) return false;
  const tag = (e.target?.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  return !e.target?.isContentEditable;
}

/** The count badge on the decisions button. (The Alerts button followed the same rule until
 *  SPEC-509 merged the bell into it; it now uses `alertsBadge`.) `null` = no badge. A failed load is NOT zero: it shows "!" so
 *  the reader is never told "nothing waiting" on a fetch error. */
export function decisionsBadge(count: number, error: boolean): string | null {
  if (error) return '!';
  if (count < 1) return null;
  return count > 9 ? '9+' : String(count);
}

/* ── The top bar's right cluster (SPEC-514) ──────────────────────────────────────── */

/** The top bar is ONE fixed row: `[staging chip · workspace · view-as · Ask SuryaIQ]`. It
 *  sits LEFT of whatever owns the right edge below it, so it never covers the "About this
 *  screen" panel or its open button (Param, 07 Oct: the Ask field overlaid that panel).
 *  `panelWidth` is the docked context panel's current width (open panel, closed strip, or 0
 *  when it does not dock); `chatWidth` is the open drawer's width (0 when closed). */
export const TOP_BAR_GUTTER = 16;
export function topBarRightOffset(panelWidth: number, chatWidth: number): number {
  const safe = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);
  return TOP_BAR_GUTTER + safe(panelWidth) + safe(chatWidth);
}

/** The Ask SuryaIQ trigger's class contract (asserted in shell.test.ts):
 *   - it never shrinks, so a crowded cluster can never squeeze it over a neighbour;
 *   - the words "Ask SuryaIQ" show from md; below md it is the mark only;
 *   - the "/" key hint shows from lg only. */
export const ASK_TRIGGER_CLASS =
  'group flex-shrink-0 inline-flex items-center justify-center gap-2 h-10 min-w-[40px] md:h-9 pl-1.5 pr-1.5 md:pr-2.5 rounded-lg ' +
  'bg-[var(--bg-raised)] border border-[var(--border-default)] text-[var(--text-primary)] ' +
  'hover:border-amber-500/50 hover:bg-[var(--bg-overlay)] focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors motion-reduce:transition-none';
export const ASK_TRIGGER_LABEL_CLASS = 'hidden md:inline text-xs font-semibold whitespace-nowrap';
export const ASK_TRIGGER_KBD_CLASS =
  'hidden lg:inline-grid place-items-center min-w-[20px] h-5 px-1 rounded border border-[var(--border-emphasis)] ' +
  'bg-[var(--bg-base)] font-mono text-[11px] text-[var(--text-secondary)]';
